import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../auth/AuthProvider';
import { applyRailState, readRailState, writeRailState, type RailMode, type RailState } from '../rail-state';
import { takeFragmentToken, useRoute } from '../router';
import { AccountPage } from './AccountPage';
import { BillingReturn } from './BillingReturn';
import { LoginCard } from './LoginCard';
import { NeedsPlan, NeedsSeat } from './Guard';
import { OrgAdminPage } from './OrgAdminPage';
import { OrgPicker } from './OrgOnboarding';
import { PlanSelector } from './PlanSelector';
import { ThemeToggle } from './ThemeToggle';
import { HuacaMark, HuacaWordmark } from './Brand';
import { SideRail, visibleItems, type NavItem } from './SideRail';
import { BuildingIcon, PowerIcon, UserIcon } from './icons';

/**
 * Everything an override slot needs to render its OWN version of a screen while leaving the platform
 * in charge of WHEN that screen shows. A custom renderer gets the same navigation levers the default
 * uses — so it can drive the flow, but not redefine it.
 */
export interface ModuleScreenContext {
  productId: string;
  productName: string;
  /** productId → module role ids, exactly as passed to ModuleApp. */
  moduleRoles: Record<string, string[]>;
  /** Client-side navigate within this module (e.g. '/plans', '/', '/account'). */
  navigate: (to: string) => void;
  /** Shortcut for navigate('/plans'). */
  showPlans: () => void;
  /** Re-fetch /auth/me — after an out-of-band change (an admin granted a seat, a plan activated). */
  reload: () => Promise<void>;
}

export interface ModuleAppProps {
  /** MUST equal the backend module id in src/manifest.ts. */
  productId: string;
  productName: string;
  /** productId → module role ids offered in org admin (mirrors the module's roles.ts). */
  moduleRoles?: Record<string, string[]>;
  /** Your product UI. Rendered only once the caller is entitled AND seated. */
  renderProduct: (ctx: ModuleScreenContext) => ReactNode;
  /**
   * This module's own destinations, above the platform's block in the side rail.
   *
   * A DESCRIPTOR rather than a render function, and the reason is `can()`. claro's real nav item is
   * permission-gated, and `can` only works inside a component under `AuthProvider` — a module-level
   * constant cannot call it. So an item carries a permission STRING and the shell evaluates it,
   * which also means one place decides what a person may see instead of each module deciding again.
   */
  navItems?: readonly NavItem[];
  /**
   * How this module's expanded rail behaves — `push` (default) gives it a grid column, `overlay`
   * floats it over the content with a scrim and never reflows the page.
   *
   * Not a taste setting. Overlay is a MODAL surface: it traps focus, closes on Escape, and covers
   * what is behind it. A module whose main screen owns the edge — a search with its own facet
   * sidebar — is the case it exists for; a module with a plain centred column should stay on push,
   * where nothing is covered and nothing has to be dismissed.
   */
  railMode?: RailMode;
  /**
   * The mark in the nav, when the org has not white-labelled with its own logo.
   *
   * Defaults to `HuacaMark` — the platform's pyramid — so a module that says nothing renders
   * exactly as it did before this existed. A module with a brand of its OWN passes it here, and
   * then a signed-in user sees the same mark the anonymous landing page showed them, instead of the
   * identity changing at the moment they log in.
   *
   * NOT a `render*` slot: those swap a whole SCREEN and take the navigation context. This swaps one
   * glyph inside a screen the platform still owns, so it is a plain node.
   */
  brandMark?: ReactNode;

  /**
   * Per-screen override slots. Each is OPTIONAL and falls back to the shared @huaca/ui default when
   * absent, so a plain module stays a few lines. A slot changes only what a screen LOOKS like — the
   * platform still decides which screen shows and when (login → org → plan → seat → product). A
   * module wanting a bespoke login page or plan layout overrides just that one slot, and cannot
   * touch or break the flow logic doing so.
   */
  renderLogin?: (ctx: ModuleScreenContext) => ReactNode;
  renderOrgPicker?: (ctx: ModuleScreenContext) => ReactNode;
  renderPlans?: (ctx: ModuleScreenContext) => ReactNode;
  renderBillingReturn?: (ctx: ModuleScreenContext) => ReactNode;
  renderOrgAdmin?: (ctx: ModuleScreenContext) => ReactNode;
  renderAccount?: (ctx: ModuleScreenContext) => ReactNode;
  renderNeedsSeat?: (ctx: ModuleScreenContext) => ReactNode;
  renderNeedsPlan?: (ctx: ModuleScreenContext) => ReactNode;
}

/**
 * The whole module shell: boot → login → org → plan → product, plus /account, /org, /plans,
 * /billing/return and both link-consumption paths.
 *
 * This lives in the PLATFORM, not in each app, because none of it is product-specific. Every module
 * would otherwise carry a byte-identical copy, and a change to a platform flow (say the invite path)
 * would mean editing every app and hoping none drifted. A module supplies `renderProduct`; the
 * sequence in front of it is Huaca's.
 */
export function ModuleApp({
  productId,
  productName,
  moduleRoles = {},
  renderProduct,
  navItems = [],
  railMode = 'push',
  brandMark,
  renderLogin,
  renderOrgPicker,
  renderPlans,
  renderBillingReturn,
  renderOrgAdmin,
  renderAccount,
  renderNeedsSeat,
  renderNeedsPlan,
}: ModuleAppProps) {
  const { phase, me, isSeated, reload, client, can, logout } = useSession();
  const { path, navigate } = useRoute();
  const [linkError, setLinkError] = useState<string | null>(null);

  /**
   * The rail's expanded/collapsed state, seeded from storage on the FIRST render rather than in an
   * effect. An effect would mean one painted frame at the default width followed by a correction —
   * which for a colour is a flash and for a grid column is the whole page jumping sideways.
   */
  const [rail, setRail] = useState<RailState>(() =>
    typeof document === 'undefined' ? 'collapsed' : readRailState(railMode),
  );

  // Publish it where module stylesheets can read it. index.html has already written the same
  // attribute before paint; this keeps it true after a toggle, and covers a document the inline
  // script never ran on (a test, an embedded host).
  useEffect(() => {
    applyRailState(rail, railMode);
  }, [rail, railMode]);

  const toggleRail = useCallback(() => {
    setRail((current) => {
      const next: RailState = current === 'collapsed' ? 'expanded' : 'collapsed';
      writeRailState(next);
      return next;
    });
  }, []);

  // Does the ORG hold this product? Presence IS the answer: /auth/me lists ONLY subscriptions that
  // currently grant (findGrantingSubscriptions), with a lapsed trial already dropped at read time.
  //
  // Deliberately NOT `status === 'active' || 'on_trial'`: the server also grants on 'past_due',
  // 'unpaid' and 'cancelled'-before-period-end. Re-deriving that list here would tell a customer
  // whose card just failed that their org has no plan — while the API happily serves them.
  // Don't restate the server's rule; trust what it sent.
  const entitled = (me?.subscriptions ?? []).some((s) => s.productId === productId);

  /**
   * Has this mount already taken the token from the URL fragment?
   *
   * `takeFragmentToken()` is destructive BY DESIGN — it strips the fragment on first read so the
   * token never lingers in the address bar, in history, or in a `Referer`. That makes it
   * single-use per mount, while the effect below re-runs whenever its dependencies change.
   *
   * And one of them changes constantly: `reload` is an inline arrow rebuilt inside AuthProvider's
   * `useMemo` on every session state change, so consuming the link — which calls `reload()` — is
   * itself enough to re-run this effect. On that re-run the token is already gone, `takeFragmentToken`
   * returns null, and control falls straight into `navigate('/login')`: the user is bounced to the
   * login screen in the middle of a login that is succeeding.
   *
   * A ref, not a dependency-array change. Dropping `reload` from the deps would only hide the
   * staleness behind a lint suppression; the token genuinely is once-per-mount, so that is what
   * gets recorded.
   */
  const linkConsumed = useRef(false);

  /**
   * Magic-link and invite links both arrive as `#token=…`. ONE handler for both: the server claims
   * pending invitations on every login, so an invite link is just a magic link that also joins an
   * org. There is deliberately no separate accept flow to keep in sync.
   */
  useEffect(() => {
    if (path !== '/auth/magic' && path !== '/invite') return;
    if (linkConsumed.current) return;
    linkConsumed.current = true;

    const token = takeFragmentToken();
    if (!token) {
      navigate('/login');
      return;
    }
    void client
      .consumeMagicLink(token)
      .then(() => reload())
      .then(() => navigate('/'))
      .catch(() => setLinkError('הקישור פג תוקף או כבר נעשה בו שימוש. בקשו קישור חדש.'));
  }, [path, client, reload, navigate]);

  /**
   * Google returns here with ?status= and NO token — the session rides the HttpOnly cookie.
   *
   * The backend redirects here ONLY on success, with status = `ok` | `needs_org` | `select_org` — and
   * ALL THREE mean "signed in". A new user's org is auto-created server-side, so `ok` is the norm;
   * `select_org` means they belong to several orgs and one must be chosen (→ OrgPicker after reload()),
   * and `needs_org` only appears in the rare case auto-create failed (→ login screen; re-login retries).
   * A Google denial or bad OAuth state comes back as a JSON error from the backend, never a redirect
   * here, so there is no error status to catch — we just reload() and let the phase machine route.
   */
  useEffect(() => {
    if (path !== '/auth/callback') return;
    void reload().then(() => navigate('/'));
  }, [path, reload, navigate]);

  if (phase === 'loading')
    return (
      <div className="huaca-auth">
        <p data-testid="booting">טוען…</p>
      </div>
    );

  // Built once and handed to every override slot — the platform's navigation levers, so a custom
  // screen can drive the flow without redefining it.
  const ctx: ModuleScreenContext = {
    productId,
    productName,
    moduleRoles,
    navigate,
    showPlans: () => navigate('/plans'),
    reload,
  };
  // Lazy on purpose: called only inside the two branches that render login (below), so a custom
  // renderLogin executes ONLY on the login/linkError screens — never eagerly on every render, which
  // would let a throwing login renderer take down unrelated screens. Matches how the other 8 slots
  // are invoked strictly inside their own branch.
  const loginScreen = () =>
    renderLogin ? renderLogin(ctx) : <LoginCard productName={productName} logo={<HuacaWordmark />} />;
  // Shared by the /plans route and the default branch's owner-without-a-plan case, so both honour the
  // renderPlans override slot identically.
  const plansScreen = () =>
    renderPlans ? (
      renderPlans(ctx)
    ) : (
      <PlanSelector
        productId={productId}
        checkoutReturnUrl={`${window.location.origin}/billing/return`}
        onEntitled={() => navigate('/')}
      />
    );

  if (linkError) {
    return (
      <div className="huaca-auth">
        <div className="huaca-auth-box">
          <p role="alert" data-testid="link-error">{linkError}</p>
          {loginScreen()}
        </div>
      </div>
    );
  }

  if (phase === 'anonymous') {
    // Mid-consumption of a link — don't flash a login form at someone who IS signing in.
    if (path === '/auth/magic' || path === '/invite' || path === '/auth/callback') {
      return (
        <div className="huaca-auth">
          <p data-testid="signing-in">מתחברים…</p>
        </div>
      );
    }
    // THIS is the module's own login page: same shared component (or its override), this subdomain.
    return (
      <div className="huaca-auth">
        <div className="huaca-auth-box">
          {loginScreen()}
        </div>
      </div>
    );
  }

  // A 200-from-/auth/refresh-with-a-NULL-token state: the user belongs to several orgs and must pick
  // one. (There is no more `needs-org` phase — a new user's org is auto-created at sign-in.)
  if (phase === 'select-org')
    return (
      <div className="huaca-auth">
        <div className="huaca-auth-box">
          {renderOrgPicker ? renderOrgPicker(ctx) : <OrgPicker />}
        </div>
      </div>
    );

  /**
   * The platform's own rail items, identical in every module.
   *
   * ORDER IS DOM ORDER HERE, unlike the row this replaced. The old header laid these out in reverse
   * because an RTL flex row renders its first child furthest right; a vertical rail reads top to
   * bottom in both directions, so the source now says what the screen says.
   */
  const sharedItems: NavItem[] = [
    {
      path: '/org',
      // The tenant's name, which the old header could only put in an aria-label because an icon row
      // had no visible slot for it. A rail has one, and in a multi-tenant product WHICH org you are
      // acting in is worth a permanent line.
      label: me?.org.name ?? 'ארגון',
      icon: <BuildingIcon />,
      // Gated, where the header showed it to everyone. A plain `member` holds only `org:profile:read`
      // and lands on a forbidden screen — tolerable as a small icon nobody notices, a visible defect
      // as a labelled row that fails every time. This is the permission the page itself checks.
      permission: 'member:list:read',
    },
    { path: '/account', label: 'חשבון', icon: <UserIcon /> },
    { label: 'התנתקות', icon: <PowerIcon />, onSelect: () => void logout() },
  ];

  const shell = (children: ReactNode) => (
    <div className="huaca-shell">
      <SideRail
        items={visibleItems(navItems, can)}
        footer={visibleItems(sharedItems, can)}
        collapsed={rail === 'collapsed'}
        onToggle={toggleRail}
        railMode={railMode}
        brandHref="/"
        brandName={productName}
        brand={
          /* White-label: the firm's logo replaces the mark when the org set one. THE ORG'S LOGO
             STILL WINS over a module's own `brandMark` — the precedence is customer, then module,
             then platform. Kept mounted when the rail collapses: the URL is signed and expires in
             five minutes, so a remount after that would 403 a paying tenant's logo. */
          me?.org.logoUrl ? (
            <img className="huaca-brand-logo" src={me.org.logoUrl} alt="" data-testid="org-brand-logo" />
          ) : (
            (brandMark ?? <HuacaMark size={22} />)
          )
        }
      >
        {/* The rail variant: a labelled full-width row, not the bordered square. The square was
            built for a horizontal top bar and, dropped into a column of rows, was the only control
            in the group with a border, the only one without text, and the only one that did not
            reach the icon column. */}
        <ThemeToggle variant="rail" />
      </SideRail>
      <main className="huaca-content">
        {children}
      </main>
    </div>
  );

  switch (path) {
    case '/account':
      return shell(renderAccount ? renderAccount(ctx) : <AccountPage />);
    case '/org':
      return shell(
        renderOrgAdmin ? renderOrgAdmin(ctx) : <OrgAdminPage productIds={[productId]} moduleRoles={moduleRoles} />,
      );
    case '/plans':
      return shell(plansScreen());
    case '/billing/return':
      return shell(
        renderBillingReturn ? renderBillingReturn(ctx) : <BillingReturn productId={productId} onEntitled={() => navigate('/')} />,
      );
    default:
      // An invited, seated member lands HERE on their first login — no plans page, no org creation,
      // no waiting on an admin. That is the entire point of promising the seat at invite time.
      if (isSeated(productId)) return shell(renderProduct(ctx));

      // Unseated. WHY matters: the branches have different fixes, applied by different people. All
      // read /auth/me (server truth), never a role-name guess.
      //   granting subscription    → the PERSON needs a seat (any admin can grant it)
      //   no plan + can buy (owner) → show plans IMMEDIATELY (the plan choice makes it single/team)
      //   no plan + cannot buy      → a member is told to ask the owner
      if (entitled) return shell(renderNeedsSeat ? renderNeedsSeat(ctx) : <NeedsSeat onRetry={reload} />);
      if (can('billing:checkout:create')) return shell(plansScreen());
      return shell(
        renderNeedsPlan ? renderNeedsPlan(ctx) : <NeedsPlan onShowPlans={() => navigate('/plans')} onRetry={reload} />,
      );
  }
}
